import { useState, useMemo } from 'react';
import { Card, Button, Table, Form, Badge } from 'react-bootstrap';
import { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.service';
import Loading from '@/shared/components/Carregando';
import RelatorioHistoricoModal from '@/modules/reports/presentation/components/RelatorioHistoricoModal';
import { getPrazoEnvioInfo } from '../utils/prazo-envio-utils';

interface RelatoriosCorregedoriaListProps {
  relatorios: RelatorioAtividadeDto[];
  loading?: boolean;
  onView: (relatorio: RelatorioAtividadeDto) => void;
  onApprove: (relatorio: RelatorioAtividadeDto) => void;
  onReject: (relatorio: RelatorioAtividadeDto) => void;
  onApproveBatch?: (relatorios: RelatorioAtividadeDto[]) => void;
  approvingBatch?: boolean;
}

export default function RelatoriosCorregedoriaList({
  relatorios = [],
  loading = false,
  onView,
  onApprove,
  onReject,
  onApproveBatch,
  approvingBatch = false,
}: RelatoriosCorregedoriaListProps) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [relatorioHistorico, setRelatorioHistorico] = useState<RelatorioAtividadeDto | null>(null);

  const aprovareis = useMemo(() => relatorios.filter((r) => !r.corregedoriaAprovadoEm), [relatorios]);
  const allAprovaveisSelected = aprovareis.length > 0 && aprovareis.every((r) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0;

  const toggleOne = (id: number, aprovarel: boolean) => {
    if (!aprovarel) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllAprovaveis = () => {
    if (allAprovaveisSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(aprovareis.map((r) => r.id)));
    }
  };

  const handleAprovarSelecionados = () => {
    if (!onApproveBatch || selectedIds.size === 0) return;
    const selecionados = relatorios.filter((r) => selectedIds.has(r.id));
    onApproveBatch(selecionados);
    setSelectedIds(new Set());
  };
  const getTipoLabel = (tipo: string) => (tipo === 'civil' ? 'Cível' : 'Criminal');

  const formatarData = (data: string | Date | undefined) => {
    if (!data) return '-';
    try {
      const date = data instanceof Date ? data : new Date(data);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  const statusAval = (r: RelatorioAtividadeDto) => {
    if (r.corregedoriaAprovadoEm) {
      return {
        label: 'Aprovado pela Corregedoria (final)',
        data: r.corregedoriaAprovadoEm,
        nome: r.corregedoriaAprovadoPorNome,
      };
    }

    if (r.status === 'aprovado') {
      return { label: 'Aguardando aval da Corregedoria', data: null, nome: undefined };
    }

    if (r.status === 'reprovado') {
      return { label: 'Reprovado pela Corregedoria', data: r.dataReprovacao, nome: r.reprovadoPorNome };
    }

    return { label: `Status atual: ${r.status}`, data: null, nome: undefined };
  };

  const getPrazoForaBadge = (relatorio: RelatorioAtividadeDto) => {
    const info = getPrazoEnvioInfo(relatorio);
    if (!info?.isForaPrazo) return null;

    const tooltip = `Prazo final: ${info.prazoFinalLabel}${info.enviadoEmLabel ? ` | Enviado em: ${info.enviadoEmLabel}` : ''}`;
    return (
      <span>
        <Badge bg="warning" text="dark" title={tooltip}>
          Fora do prazo
        </Badge>
      </span>
    );
  };

  /** Indicador no Visualizar: ainda há remoto ou ausência sem decisão da Corregedoria. */
  const diasAverbacaoPendenteNoRelatorio = (relatorio: RelatorioAtividadeDto) => {
    const dias =
      (relatorio.dados?.comparecimento?.diasStatus as Array<{
        status?: string;
        remotoValidado?: boolean;
        ausenteAceitoCorregedoria?: boolean;
      }>) || [];

    return dias.some(
      (d) =>
        (d.status === 'remoto' && d.remotoValidado !== true) ||
        (d.status === 'ausente' && d.ausenteAceitoCorregedoria !== true),
    );
  };

  if (loading) {
    return <Loading />;
  }

  if (relatorios.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center text-muted">
          <p className="mb-0">Nenhum relatório aguardando aval da Corregedoria.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <style>{`
        @keyframes triBlink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.25; transform: scale(1.06); }
        }
        .blink-tri {
          animation: triBlink 1s infinite ease-in-out;
        }
      `}</style>
      <Card className="shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 className="mb-0">Relatórios</h5>
        {onApproveBatch && aprovareis.length > 0 && (
          <Button
            variant="primary"
            size="sm"
            disabled={!someSelected || approvingBatch}
            onClick={handleAprovarSelecionados}
          >
            {approvingBatch ? 'Aprovando...' : `Aprovar selecionados (${selectedIds.size})`}
          </Button>
        )}
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table striped hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th style={{ width: '40px' }}>
                  {aprovareis.length > 0 && (
                    <Form.Check
                      type="checkbox"
                      checked={allAprovaveisSelected}
                      onChange={toggleAllAprovaveis}
                      aria-label="Selecionar todos aguardando aval"
                    />
                  )}
                </th>
                <th>Defensor</th>
                <th>Defensoria/Vara</th>
                <th>Período</th>
                <th>Tipo</th>
                <th>Data aprovação (Corregedoria)</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {relatorios.map((relatorio) => {
                const aval = statusAval(relatorio);
                const aguardando = !relatorio.corregedoriaAprovadoEm;
                return (
                  <tr key={relatorio.id}>
                    <td>
                      {aguardando ? (
                        <Form.Check
                          type="checkbox"
                          checked={selectedIds.has(relatorio.id)}
                          onChange={() => toggleOne(relatorio.id, true)}
                          aria-label={`Selecionar relatório ${relatorio.periodo}`}
                        />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{relatorio.defensorNome || 'Não informado'}</td>
                    <td>{relatorio.defensoriaName}</td>
                    <td>{relatorio.periodo}</td>
                    <td>{getTipoLabel(relatorio.tipo)}</td>
                    <td>{formatarData(relatorio.corregedoriaAprovadoEm)}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <span>{aval.label}</span>
                        {getPrazoForaBadge(relatorio)}
                      </div>
                      {relatorio.dataEnvio && (
                        <>
                          <small className="text-muted">
                            Data de envio: {formatarData(relatorio.dataEnvio)}
                          </small>
                          {aval.nome && <small className="d-block text-muted">{aval.nome}</small>}
                        </>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap" role="group">
                        <div style={{ position: 'relative', display: 'inline-flex', overflow: 'visible' }}>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => onView(relatorio)}
                          >
                            Visualizar
                          </Button>
                          {diasAverbacaoPendenteNoRelatorio(relatorio) && (
                            <i
                              className="bi bi-exclamation-triangle-fill blink-tri"
                              style={{
                                position: 'absolute',
                                top: '0px',
                                left: '0px',
                                transform: 'translate(-35%, -35%)',
                                color: '#212529',
                                fontSize: '1.05rem',
                                fontFamily: 'bootstrap-icons',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: 1,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#ffc107',
                                borderRadius: '50%',
                                padding: '3px',
                                minWidth: '20px',
                                minHeight: '20px',
                                zIndex: 9999,
                                pointerEvents: 'none',
                                boxShadow: '0 0 0 2px rgba(255,255,255,0.95)',
                                textShadow: '0 0 1px rgba(0,0,0,0.25)',
                              }}
                              aria-hidden
                            />
                          )}
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="rounded"
                          onClick={() => setRelatorioHistorico(relatorio)}
                          title="Ver histórico de tramitação (defensor, diretor, Corregedoria)"
                        >
                          <i className="bi bi-clock-history me-1"></i>
                          Histórico
                        </Button>
                        {aguardando && (
                          <>
                            <Button variant="outline-success" size="sm" onClick={() => onApprove(relatorio)}>
                              Aprovar
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => onReject(relatorio)}>
                              Reprovar
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>

      <RelatorioHistoricoModal
        show={relatorioHistorico !== null}
        relatorio={relatorioHistorico}
        onHide={() => setRelatorioHistorico(null)}
      />
      </Card>
    </>
  );
}
